import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';

import * as customPropTypes from '@/custom-prop-types';

import * as Card from '../components';

/**
 * Search Results Card - Clinical Trial
 * @param {object} props
 * @param {boolean} props.isInternal - Is internal link
 * @param {object} props.analytics - Analytics object
 * @param {object} props.badge - Badge label
 * @param {object} props.link - Link object
 * @param {object} props.status - Status object
 * @param {string} props.className - Additional class name
 * @param {string} props.title - Title
 * @returns {React.ReactElement}
 */
export const SearchResultsCardTrial = React.forwardRef(
    (
        {
            analytics,
            badge,
            className,
            isInternal,
            link,
            status,
            title,
            coveoAnalyticsCallbacks,
            ...props
        },
        ref,
    ) => {
        const analyticsObj = analytics
            ? {
                  ...analytics,
                  componentTitle: badge?.label,
                  trialName: title,
              }
            : undefined;

        const handleClick = useCallback(() => {
            coveoAnalyticsCallbacks?.onClick?.();
        }, [coveoAnalyticsCallbacks]);

        const handleContextMenu = useCallback(() => {
            coveoAnalyticsCallbacks?.onContextMenu?.();
        }, [coveoAnalyticsCallbacks]);

        const handleMouseDown = useCallback(() => {
            coveoAnalyticsCallbacks?.onMouseDown?.();
        }, [coveoAnalyticsCallbacks]);

        const handleMouseUp = useCallback(() => {
            coveoAnalyticsCallbacks?.onMouseUp?.();
        }, [coveoAnalyticsCallbacks]);

        const handleTouchStart = useCallback(() => {
            coveoAnalyticsCallbacks?.onTouchStart?.();
        }, [coveoAnalyticsCallbacks]);

        const handleTouchEnd = useCallback(() => {
            coveoAnalyticsCallbacks?.onTouchEnd?.();
        }, [coveoAnalyticsCallbacks]);

        useEffect(() => {
            coveoAnalyticsCallbacks?.cancelPendingSelect?.();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        return (
            <Card.Container
                className={className}
                ref={ref}
                {...props}
                analytics={analyticsObj}
                badge={badge}
                status={status}
            >
                <Card.Header>
                    {badge?.type && badge?.label && (
                        <Card.Badge type={badge.type} label={badge.label} />
                    )}
                </Card.Header>
                <Card.Content>
                    <Card.Title
                        analytics={analyticsObj}
                        isInternal={isInternal}
                        link={link}
                        title={title}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={coveoAnalyticsCallbacks ? handleClick : undefined}
                        onContextMenu={coveoAnalyticsCallbacks ? handleContextMenu : undefined}
                        onMouseDown={coveoAnalyticsCallbacks ? handleMouseDown : undefined}
                        onMouseUp={coveoAnalyticsCallbacks ? handleMouseUp : undefined}
                        onTouchEnd={coveoAnalyticsCallbacks ? handleTouchEnd : undefined}
                        onTouchStart={coveoAnalyticsCallbacks ? handleTouchStart : undefined}
                    />
                    {status && <Card.Status {...status} />}
                </Card.Content>
            </Card.Container>
        );
    },
);

SearchResultsCardTrial.defaultProps = {
    badge: {
        type: 'trial',
        label: 'Clinical Trial',
    },
};

SearchResultsCardTrial.propTypes = {
    analytics: customPropTypes.analyticsPropType,
    badge: PropTypes.shape({
        type: PropTypes.string,
        label: PropTypes.string,
    }),
    className: PropTypes.string,
    isInternal: PropTypes.bool,
    link: PropTypes.shape({
        href: PropTypes.string,
        target: PropTypes.string,
        rel: PropTypes.string,
        csr: PropTypes.bool,
        ariaLabel: PropTypes.string,
    }),
    status: PropTypes.shape({
        label: PropTypes.string,
        isActive: PropTypes.bool,
    }),
    title: PropTypes.string,
};
